import {
    DateField,
    EditButton,
    NumberField,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, FolderOpen, Loader2, Target, TrendingUp } from "lucide-react";
import { useGetList, useNotify, useRecordContext, useUpdate } from 'ra-core';
import { useNavigate } from 'react-router';
import { ActivityTimeline } from '../activities/ActivityTimeline';
import { useHasPermission } from '../rbac/useHasPermission';

const STAGES = [
    { id: 'inquiry', label: '1. Inquiry' },
    { id: 'requirement_discussion', label: '2. Requirement Disc.' },
    { id: 'proposal', label: '3. Proposal' }
];

const PipelineTracker = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [update, { isPending }] = useUpdate();

    if (!record?.stage) return null;

    if (record.stage === 'won') {
        return <div className="p-4 bg-success/10 border border-success/30 text-success font-bold rounded-lg text-center">🏆 Opportunity Won</div>;
    }
    if (record.stage === 'lost') {
        return (
            <div className="p-4 bg-destructive/10 border border-destructive/30 text-destructive rounded-lg text-center">
                <div className="font-bold">❌ Opportunity Lost</div>
                {record.lost_reason && <div className="text-sm mt-1 max-w-xl mx-auto italic">{record.lost_reason}</div>}
            </div>
        );
    }

    const currentIndex = STAGES.findIndex(s => s.id === record.stage) ?? 0;

    const handleStageClick = (stageId: string, index: number) => {
        if (stageId === record.stage || isPending) return;
        update(
            'opportunities',
            { id: record.id, data: { stage: stageId }, previousData: record },
            {
                onSuccess: () => notify(`Stage updated to ${STAGES[index].label}`, { type: 'success' }),
                onError: () => notify('Failed to update stage', { type: 'error' }),
            }
        );
    };

    return (
        <div className="flex items-center justify-between mx-auto max-w-3xl mt-6 px-4">
            {STAGES.map((stage, index) => {
                const isActive = index === currentIndex;
                const isPast = index < currentIndex;
                const isClickable = stage.id !== record.stage && !isPending;
                const circleClass = isActive
                    ? 'bg-info text-info-foreground border-info'
                    : isPast
                    ? 'bg-info text-info-foreground border-info'
                    : 'bg-card text-muted-foreground border-border';
                const lineClass = isPast ? 'bg-info' : 'bg-border';

                return (
                    <div key={stage.id} className="relative flex-1 flex flex-col items-center">
                        {index > 0 && (
                            <div className={`absolute left-0 top-4 -z-10 h-1 w-full -translate-x-1/2 -translate-y-1/2 ${lineClass}`} />
                        )}
                        <button
                            type="button"
                            onClick={() => handleStageClick(stage.id, index)}
                            disabled={!isClickable}
                            title={isClickable ? `Move to ${stage.label}` : undefined}
                            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-sm z-10 transition-all
                                ${circleClass}
                                ${isClickable ? 'cursor-pointer hover:scale-110 hover:shadow-md' : 'cursor-default'}
                                ${isActive ? 'ring-2 ring-info/40 ring-offset-1' : ''}
                            `}
                        >
                            {isPending && isActive ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : isPast ? '✓' : index + 1}
                        </button>
                        <div className={`text-xs mt-2 font-medium ${isActive || isPast ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {stage.label}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const ConvertToProjectButton = () => {
    const record = useRecordContext();
    const navigate = useNavigate();
    const { hasPermission: canManage } = useHasPermission('manage_deals');
    const { data: existingProjects } = useGetList('projects', {
        pagination: { page: 1, perPage: 1 },
        filter: { enquiry_id: record?.enquiry_id },
    }, { enabled: !!record?.enquiry_id });
    const alreadyConverted = !!record?.enquiry_id && (existingProjects?.length ?? 0) > 0;

    if (!record || record.stage !== 'won' || !canManage) return null;

    const handleConvert = () => {
        if (alreadyConverted) return;
        navigate('/projects/create', {
            state: {
                record: {
                    company_id: record.company_id,
                    contact_id: record.contact_id,
                    enquiry_id: record.enquiry_id,
                    estimated_value: record.deal_value,
                    description: `Converted from Opportunity ${record.reference}`,
                    project_type: 'new_order',
                }
            }
        });
    };

    return (
        <Button
            onClick={handleConvert}
            disabled={alreadyConverted}
            title={alreadyConverted ? 'A project already exists for this opportunity' : undefined}
            className="gap-2 bg-success hover:bg-success/90 text-success-foreground disabled:opacity-50"
            size="sm"
        >
            <FolderOpen className="w-4 h-4" />
            {alreadyConverted ? 'Project Already Created' : 'Convert to Project'}
        </Button>
    );
};

const OpportunityShowContent = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <div className="p-6 space-y-6 max-w-5xl mx-auto">
            <div className="flex justify-between items-start">
                <div className="space-y-1">
                    <h2 className="text-3xl font-bold tracking-tight">{record.reference}</h2>
                    <p className="text-sm text-muted-foreground">
                        Created on <DateField source="created_at" className="font-medium" />
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <ConvertToProjectButton />
                    <Badge variant="outline" className={`font-semibold ${record.status === 'open' ? 'bg-success/10 text-success' : 'bg-muted text-foreground'}`}>
                        {record.status?.toUpperCase()}
                    </Badge>
                </div>
            </div>

            <PipelineTracker />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <Card className="shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Customer Information</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-muted-foreground uppercase">Company</span>
                            <ReferenceField source="company_id" reference="companies" link="show">
                                <TextField source="name" className="text-base font-bold block text-primary hover:underline" />
                            </ReferenceField>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-muted-foreground uppercase">Contact Person</span>
                            <ReferenceField source="contact_id" reference="contacts" link="show">
                                <div className="text-sm font-medium">
                                    <TextField source="first_name" /> <TextField source="last_name" />
                                </div>
                            </ReferenceField>
                        </div>
                        {record.enquiry_id && (
                            <div className="space-y-1">
                                <span className="text-xs font-bold text-muted-foreground uppercase">Linked Enquiry</span>
                                <ReferenceField source="enquiry_id" reference="enquiries" link="show">
                                    <TextField source="reference" className="text-sm text-info hover:underline" />
                                </ReferenceField>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="shadow-sm border-t-4 border-t-success">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-success" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Deal Valuation</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="flex justify-between items-center bg-success/10 text-success p-4 rounded-lg font-semibold text-lg">
                            <span>Expected Value</span>
                            <div>₹<NumberField source="deal_value" options={{ maximumFractionDigits: 0 }} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                             <div className="space-y-1 p-4 border rounded-lg">
                                <span className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase"><TrendingUp className="w-4 h-4" /> Win Prob.</span>
                                <div className="text-xl font-bold"><NumberField source="win_probability" />%</div>
                             </div>
                             <div className="space-y-1 p-4 border rounded-lg">
                                <span className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase"><Target className="w-4 h-4" /> Close Date</span>
                                <div className="text-sm font-semibold pt-1"><DateField source="expected_close_date" /></div>
                             </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="mt-8 h-[600px]">
                <ActivityTimeline resource="opportunities" recordId={record.id} />
            </div>
        </div>
    );
};

export const OpportunityShow = () => (
    <Show actions={<div className="flex justify-end gap-2 p-4"><EditButton /></div>}>
        <OpportunityShowContent />
    </Show>
);
